import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, Send, Sparkles, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { PRICING_TIERS, SERVICES } from "@/lib/content";

const BUDGETS = ["Under R10,000","R10,000 – R25,000","R25,000 – R50,000","R50,000 – R100,000","R100,000 – R250,000","R250,000+"];

export default function Booking({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [params] = useSearchParams();
  const presetTier = params.get("tier") || "";
  const [form, setForm] = useState({ name:"", email:"", phone:"", company:"", service_category:"", package_tier:presetTier, project_description:"", budget_range:"", preferred_date:"", preferred_time:"" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const update = (k: string, v: string) => setForm({ ...form, [k]: v });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_category || !form.project_description) {
      toast.error("Please fill the required fields."); return;
    }
    setLoading(true);
    try {
      const response = await api.post("/bookings", form);
      setEmailSent(Boolean(response.data.confirmation_email_sent));
      toast.success("Consultation request received.");
      setDone(true);
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <>
        <Navbar onOpenPalette={onOpenPalette} />
        <main className="min-h-screen pt-32 pb-20 page-fade" data-testid="booking-success">
          <div className="container-edx max-w-2xl text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}
              className="mx-auto h-24 w-24 rounded-full bg-gradient-to-br from-edx-cyan via-edx-purple to-edx-pink flex items-center justify-center">
              <CheckCircle2 size={48} className="text-white" />
            </motion.div>
            <h1 className="display mt-8 text-4xl md:text-5xl text-white">You're <span className="gradient-text">in</span>.</h1>
            <p className="mt-4 text-edx-muted">We've received your consultation request. The EDENOX team will reach out within 24 hours.</p>
            <p className="mt-3 text-sm text-edx-cyan">
              {emailSent
                ? `A confirmation has been sent to ${form.email}.`
                : "Your request is saved. Email confirmation is pending while our mail service completes delivery."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn btn-primary">Back to home</Link>
              <Link to="/contact" className="btn btn-ghost">Send another message</Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="min-h-screen pt-28 pb-20 relative page-fade" data-testid="booking-page">
        <div className="absolute inset-0 edx-aurora-bg opacity-30" />
        <div className="relative container-edx max-w-5xl">
          <div className="text-center max-w-2xl mx-auto pt-12">
            <div className="section-index">EDX.01</div>
            <h1 className="display mt-4 text-4xl md:text-6xl text-white">Book a <span className="gradient-text">consultation</span>.</h1>
            <p className="mt-4 text-edx-muted">Tell us about your project. We'll respond within 24 hours with a tailored plan.</p>
          </div>

          <form onSubmit={submit} className="mt-12 glass glass-edge p-6 md:p-10" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name *"><input data-testid="booking-name" className="edx-input" value={form.name} onChange={(e) => update("name", e.target.value)} required /></Field>
              <Field label="Email *"><input data-testid="booking-email" type="email" className="edx-input" value={form.email} onChange={(e) => update("email", e.target.value)} required /></Field>
              <Field label="Phone"><input data-testid="booking-phone" className="edx-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+27 71 234 5678" /></Field>
              <Field label="Company"><input data-testid="booking-company" className="edx-input" value={form.company} onChange={(e) => update("company", e.target.value)} /></Field>
              <Field label="Service category *">
                <select data-testid="booking-service" className="edx-input" value={form.service_category} onChange={(e) => update("service_category", e.target.value)} required>
                  <option value="">Choose a service…</option>
                  {SERVICES.map((s) => <option key={s.title} value={s.title} className="bg-edx-eclipse">{s.title}</option>)}
                  <option value="Other" className="bg-edx-eclipse">Other / Not sure</option>
                </select>
              </Field>
              <Field label="Package interest">
                <select data-testid="booking-tier" className="edx-input" value={form.package_tier} onChange={(e) => update("package_tier", e.target.value)}>
                  <option value="">Choose a package…</option>
                  {PRICING_TIERS.map((t) => <option key={t.key} value={t.name} className="bg-edx-eclipse">{t.name} ({t.price})</option>)}
                  <option value="Custom Enterprise" className="bg-edx-eclipse">Custom Enterprise</option>
                </select>
              </Field>
              <Field label="Budget range">
                <select data-testid="booking-budget" className="edx-input" value={form.budget_range} onChange={(e) => update("budget_range", e.target.value)}>
                  <option value="">Select…</option>
                  {BUDGETS.map((b) => <option key={b} value={b} className="bg-edx-eclipse">{b}</option>)}
                </select>
              </Field>
              <Field label="Preferred date"><input data-testid="booking-date" type="date" className="edx-input" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} /></Field>
              <Field label="Preferred time" className="md:col-span-2">
                <select data-testid="booking-time" className="edx-input" value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)}>
                  <option value="">Anytime</option>
                  <option value="09:00 - 11:00" className="bg-edx-eclipse">Morning (09:00 – 11:00)</option>
                  <option value="11:00 - 13:00" className="bg-edx-eclipse">Late morning (11:00 – 13:00)</option>
                  <option value="14:00 - 16:00" className="bg-edx-eclipse">Afternoon (14:00 – 16:00)</option>
                  <option value="16:00 - 18:00" className="bg-edx-eclipse">Late afternoon (16:00 – 18:00)</option>
                </select>
              </Field>
              <Field label="Project description *" className="md:col-span-2">
                <textarea data-testid="booking-description" className="edx-input min-h-[140px] resize-y" value={form.project_description} onChange={(e) => update("project_description", e.target.value)} placeholder="Tell us about your project, goals, and any specific requirements…" required />
              </Field>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-between">
              <div className="text-xs text-edx-muted flex items-center gap-2 font-mono">
                <Calendar size={14} className="text-edx-cyan" /> We respond within 24 hours · Free first consultation
              </div>
              <button type="submit" data-testid="booking-submit" disabled={loading} className="btn btn-primary w-full sm:w-auto disabled:opacity-60">
                {loading ? "Sending..." : "Book consultation"} <Send size={14} />
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Field({ label, children, className = "" }: any) {
  return <div className={className}><label className="text-[11px] uppercase tracking-widest text-edx-muted block mb-2 font-mono">{label}</label>{children}</div>;
}
